import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PropTypes from 'prop-types';
import { Tooltip } from '@mui/material';
import { useState } from 'react';
import {
    ResponsiveContainer,
    Treemap,
} from 'recharts';

const FIRMWARE_VERSION_COLORS = [
    '#04c2ff',
    '#0288d1',
    '#1e40af',
    '#4f46e5',
    '#2563eb',
    '#0f766e',
    '#0369a1',
    '#4338ca',
    '#1d4ed8',
    '#312e81',
];
const INACTIVE_COLOR = '#e5e7eb';
const TREEMAP_MIN_LABEL_WIDTH = 96;
const TREEMAP_MIN_LABEL_HEIGHT = 42;

const normalizeVersion = (version) => {
    const trimmedVersion = version?.trim();

    return trimmedVersion || 'N/A';
};

const buildChartRows = (rows) => {
    const totalCases = rows.reduce((acc, row) => acc + (row.case_count || 0), 0);

    return {
        chartRows: rows.map((row, index) => ({
            caseCount: row.case_count || 0,
            color: FIRMWARE_VERSION_COLORS[index % FIRMWARE_VERSION_COLORS.length],
            firmwareVersion: normalizeVersion(row.scanner_firmware_version),
            key: row.scanner_firmware_version || `unknown-firmware-version-${index}`,
            percentage: totalCases > 0 ? ((row.case_count || 0) / totalCases) * 100 : 0,
        })),
        totalCases,
    };
};

const formatCaseCount = (value) => value.toLocaleString();
const formatPercentage = (value) => `${value.toFixed(1)}%`;
const getTreemapLabel = (label, width) => {
    const maxCharacters = Math.max(Math.floor((width - 24) / 7), 6);

    if (label.length <= maxCharacters) {
        return label;
    }

    return `${label.slice(0, Math.max(maxCharacters - 1, 1))}\u2026`;
};

const getDisplayColor = (row, activeKey) => {
    if (!activeKey || row.key === activeKey) {
        return row.color;
    }

    return INACTIVE_COLOR;
};

function TreemapTile({
    activeKey,
    caseCount,
    color,
    firmwareVersion,
    height,
    tileKey,
    width,
    x,
    y,
}) {
    const canRenderLabel = width >= TREEMAP_MIN_LABEL_WIDTH && height >= TREEMAP_MIN_LABEL_HEIGHT;
    const fill = getDisplayColor({ color, key: tileKey }, activeKey);

    return (
        <g>
            <rect
                x={x}
                y={y}
                width={width}
                height={height}
                rx={8}
                ry={8}
                fill={fill}
                stroke="#ffffff"
                strokeWidth={3}
            />
            {canRenderLabel && (
                <>
                    <text
                        x={x + 12}
                        y={y + 20}
                        fill="#ffffff"
                        fontSize="12"
                        fontWeight="600"
                    >
                        {getTreemapLabel(firmwareVersion, width)}
                    </text>
                    <text
                        x={x + 12}
                        y={y + 40}
                        fill="rgba(255, 255, 255, 0.92)"
                        fontSize="12"
                    >
                        {formatCaseCount(caseCount)}
                    </text>
                </>
            )}
        </g>
    );
}

TreemapTile.propTypes = {
    activeKey: PropTypes.string.isRequired,
    caseCount: PropTypes.number.isRequired,
    color: PropTypes.string.isRequired,
    firmwareVersion: PropTypes.string.isRequired,
    height: PropTypes.number.isRequired,
    tileKey: PropTypes.string.isRequired,
    width: PropTypes.number.isRequired,
    x: PropTypes.number.isRequired,
    y: PropTypes.number.isRequired,
};

function ScanVolumeByFirmwareVersionChart({ analytics }) {
    const [activeKey, setActiveKey] = useState('');
    const rows = analytics?.rows || [];
    const { chartRows, totalCases } = buildChartRows(rows);
    const hasRows = totalCases > 0;

    const renderTreemapTile = (node) => {
        if (node.depth !== 1) {
            return null;
        }

        return (
            <TreemapTile
                activeKey={activeKey}
                caseCount={node.caseCount}
                color={node.color}
                firmwareVersion={node.firmwareVersion}
                height={node.height}
                tileKey={node.key}
                width={node.width}
                x={node.x}
                y={node.y}
            />
        );
    };

    return (
        <section className="dashboard-chart-card">
            <div className="dashboard-chart-header dashboard-chart-header--with-icon">
                <h2>Scan Volume by Firmware Version</h2>
                <Tooltip title="Recent firmware versions by total scan volume for the selected date range. Older scans created before firmware version tracking began are not included.">
                    <InfoOutlinedIcon className="dashboard-chart-info-icon" fontSize="small" />
                </Tooltip>
            </div>
            <div className={`dashboard-chart-body${hasRows ? ' dashboard-chart-body--auto' : ''}`}>
                {hasRows ? (
                    <div className="dashboard-treemap-chart">
                        <div className="dashboard-treemap-chart__summary">
                            <span className="dashboard-treemap-chart__summary-label">Total Cases</span>
                            <span className="dashboard-treemap-chart__summary-value">
                                {formatCaseCount(totalCases)}
                            </span>
                        </div>
                        <div className="dashboard-treemap-chart__visual">
                            <ResponsiveContainer width="100%" height="100%">
                                <Treemap
                                    data={chartRows}
                                    dataKey="caseCount"
                                    nameKey="firmwareVersion"
                                    aspectRatio={1.4}
                                    isAnimationActive={false}
                                    content={renderTreemapTile}
                                    onMouseEnter={(node) => setActiveKey(node.key || '')}
                                    onMouseLeave={() => setActiveKey('')}
                                />
                            </ResponsiveContainer>
                        </div>
                        <div className="dashboard-treemap-chart__legend">
                            {chartRows.map((row) => (
                                <div
                                    key={row.key}
                                    className={`dashboard-treemap-chart__legend-row${
                                        activeKey && row.key !== activeKey
                                            ? ' dashboard-treemap-chart__legend-row--inactive'
                                            : ''
                                    }`}
                                    onMouseEnter={() => setActiveKey(row.key)}
                                    onMouseLeave={() => setActiveKey('')}
                                >
                                    <div className="dashboard-treemap-chart__legend-label">
                                        <span
                                            className="dashboard-treemap-chart__legend-dot"
                                            style={{ backgroundColor: getDisplayColor(row, activeKey) }}
                                        />
                                        <span>{row.firmwareVersion}</span>
                                    </div>
                                    <div className="dashboard-treemap-chart__legend-value">
                                        {formatCaseCount(row.caseCount)}
                                    </div>
                                    <div className="dashboard-treemap-chart__legend-percentage">
                                        {formatPercentage(row.percentage)}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ) : (
                    <div className="dashboard-chart-empty">
                        <p>No data for the selected range.</p>
                        <p>Try expanding the date range or adjusting filters.</p>
                    </div>
                )}
            </div>
        </section>
    );
}

ScanVolumeByFirmwareVersionChart.propTypes = {
    analytics: PropTypes.shape({
        rows: PropTypes.arrayOf(PropTypes.shape({
            case_count: PropTypes.number,
            scanner_firmware_version: PropTypes.string,
        })),
    }),
};

ScanVolumeByFirmwareVersionChart.defaultProps = {
    analytics: null,
};

export default ScanVolumeByFirmwareVersionChart;
